<!--
  Message thread with the Uber driver.

  Send-only in practice unless Uber delivers a reply: the portal's own messages
  are written when sent, and a driver reply arrives over the
  `guests.trips.trip_message` webhook and is pushed here live.

  A dialog rather than part of the details drawer - the drawer is already dense,
  and a conversation needs the width.
-->
<template>
  <v-dialog :value="value" width="620" scrollable @input="$emit('input', $event)">
    <v-card class="dm-card">
      <div class="dm-header">
        <div class="dm-title">
          <i class="fas fa-comment-dots"></i>
          Message the Driver
        </div>
        <div class="dm-sub">{{ driverName }}</div>
        <v-btn icon small class="dm-close" @click="$emit('input', false)">
          <i class="fas fa-times"></i>
        </v-btn>
      </div>

      <div class="dm-thread" ref="thread">
        <div class="dm-loading" v-if="loading">
          <v-progress-circular indeterminate :size="28" :width="3" color="#d59e1d"></v-progress-circular>
        </div>

        <div class="dm-empty" v-else-if="messages.length === 0">
          <i class="far fa-comments"></i>
          <span>No messages yet. Anything you send reaches the driver in the Uber app.</span>
        </div>

        <div
          v-else
          v-for="(entry, index) in messages"
          :key="entry._id || index"
          class="dm-row"
          :class="entry.sender === 'driver' ? 'from-driver' : 'from-us'"
        >
          <div class="dm-bubble">
            <!-- Three possible senders - the portal, the crew's own crew link,
                 and the driver - so each line says which. -->
            <span class="dm-who">{{ senderLabel(entry.sender) }}</span>
            <span class="dm-text">{{ entry.text }}</span>
            <span class="dm-time">{{ entry.timestamp || "" }}</span>
          </div>
        </div>
      </div>

      <div class="dm-composer">
        <v-textarea
          v-model="draft"
          placeholder="Type a message for the driver"
          rows="2"
          auto-grow
          no-resize
          outlined
          dense
          hide-details
          color="#ff9600"
          :disabled="sending"
          @keydown.enter.exact.prevent="send"
        ></v-textarea>
        <div class="dm-composer-actions">
          <!-- Uber caps a driver message; the server validates the same 500. -->
          <span class="dm-count" :class="{ over: draft.length > 500 }">{{ draft.length }}/500</span>
          <v-btn
            small
            color="#d59e1d"
            class="dm-send"
            :loading="sending"
            :disabled="!canSend"
            @click="send"
          >
            <i class="fas fa-paper-plane"></i>&nbsp; Send
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "@/axios/axios-global";
import toast from "@/components/common/toast";
import uberRealtime from "@/services/uberRealtime";

export default {
  name: "DriverMessages",
  props: {
    value: { type: Boolean, default: false },
    tripId: { type: [Number, String], default: null },
    driverName: { type: String, default: "" },
  },
  data() {
    return {
      messages: [],
      draft: "",
      loading: false,
      sending: false,
      unsubscribe: null,
    };
  },
  computed: {
    canSend() {
      const text = this.draft.trim();
      return text.length > 0 && text.length <= 500 && !this.sending;
    },
  },
  watch: {
    // Fetch when the dialog opens rather than on mount: the parent keeps this
    // component alive across trips.
    value(open) {
      if (open) this.load();
    },
  },
  mounted() {
    this.unsubscribe = uberRealtime.subscribe({ message: this.onIncoming });
    if (this.value) this.load();
  },
  beforeDestroy() {
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    async load() {
      if (!this.tripId) return;
      this.loading = true;
      try {
        const response = await axios.post("uber/trip/messages", { tripId: this.tripId });
        this.messages = (response.data && response.data.messages) || [];
        this.$nextTick(this.scrollToLatest);
      } catch (err) {
        this.messages = [];
      } finally {
        this.loading = false;
      }
    },

    async send() {
      if (!this.canSend) return;
      const text = this.draft.trim();
      this.sending = true;
      try {
        const response = await axios.post("uber/trip/message-driver", {
          tripId: this.tripId,
          message: text,
        });
        // The server returns the whole thread, so there is no need to guess
        // where the new message sits.
        this.messages = (response.data && response.data.messages) || this.messages;
        this.draft = "";
        this.$nextTick(this.scrollToLatest);
      } catch (err) {
        const data = err && err.response && err.response.data;
        toast.error(
          (data && data.message) ||
            (typeof data === "string" && data) ||
            "Could not send the message."
        );
      } finally {
        this.sending = false;
      }
    },

    /** A driver reply arriving over the socket, for this trip. */
    onIncoming(payload) {
      if (!payload || !payload.message) return;
      if (String(payload.tripId) !== String(this.tripId)) return;

      // The webhook can redeliver, and sending already added our own copy.
      const id = payload.message.messageId;
      if (id && this.messages.some((entry) => entry.messageId === id)) return;

      this.messages.push(payload.message);
      this.$nextTick(this.scrollToLatest);
    },

    scrollToLatest() {
      const el = this.$refs.thread;
      if (el) el.scrollTop = el.scrollHeight;
    },

    /**
     * Who wrote a line. "User" is the crew writing from their own ride link -
     * worth distinguishing from the office, since the two read very differently
     * to whoever is following up.
     *
     * Messages written before the senders were split all say 'user'; those are
     * portal messages that will read as "User" here.
     */
    senderLabel(sender) {
      if (sender === "driver") return "Driver";
      if (sender === "admin") return "Admin";
      return "User";
    },
  },
};
</script>

<style lang="scss" scoped>
.dm-card {
  display: flex;
  flex-direction: column;
}

.dm-header {
  position: relative;
  padding: 14px 18px;
  border-bottom: 1px solid #eeeeee;
}

.dm-title {
  font-size: 15px;
  font-weight: 600;
  color: #333333;

  i {
    color: #d59e1d;
    margin-right: 6px;
  }
}

.dm-sub {
  font-size: 12px;
  color: #8a8a8a;
  margin-top: 2px;
}

.dm-close {
  position: absolute;
  top: 10px;
  right: 10px;
  color: #8a8a8a;
}

.dm-thread {
  padding: 16px 18px;
  // Tall enough to read a conversation, capped so the composer stays visible.
  min-height: 220px;
  max-height: 46vh;
  overflow-y: auto;
  background: #fafafa;
}

.dm-loading {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}

.dm-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 34px 20px;
  text-align: center;
  color: #a0a0a0;
  font-size: 13px;

  i {
    font-size: 26px;
  }
}

.dm-row {
  display: flex;
  margin-bottom: 10px;

  &.from-us {
    justify-content: flex-end;
  }

  &.from-driver {
    justify-content: flex-start;
  }
}

.dm-bubble {
  display: flex;
  flex-direction: column;
  max-width: 76%;
  padding: 8px 12px;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0px 0px 4px 0px rgba(106, 7, 75, 0.12);
}

.from-us .dm-bubble {
  background: #fff4e0;
}

.dm-who {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: #9e9e9e;
  margin-bottom: 3px;
}

.dm-text {
  font-size: 13px;
  color: #333333;
  white-space: pre-wrap;
  word-break: break-word;
}

.dm-time {
  font-size: 10px;
  color: #9e9e9e;
  margin-top: 4px;
  align-self: flex-end;
}

.dm-composer {
  padding: 12px 18px 14px;
  border-top: 1px solid #eeeeee;
}

.dm-composer-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 10px;
}

.dm-count {
  font-size: 11px;
  color: #9e9e9e;

  &.over {
    color: #c62828;
    font-weight: 600;
  }
}

.dm-send {
  color: #ffffff !important;
  font-weight: 600;
}
</style>
